/**
 * LimitedDataBanner — friendly empty-state banner for course records
 * that carry only editorial-seed metadata (no admissions, no HESA
 * fields, no concrete cost). Instead of leaving families staring at
 * a wall of "—" cells, we tell them plainly that WUA is still
 * gathering data for this course and hand them a direct link back to
 * the parent university and UCAS.
 *
 * Placement: rendered above The Professor summary in DegreeDetail.
 */
import { Link } from "react-router-dom";
import { Info, ExternalLink } from "lucide-react";

/**
 * `d` is a stub when none of the primary data blocks carry any
 * meaningful signal:
 *   - no `admissions` object
 *   - no `hesa_stats` (HESA-imported rows already carry rich data)
 *   - no concrete tuition figure (dom or intl, USD or GBP)
 * We keep the check conservative — any of these signals present
 * means enough of the page will paint that a banner would add noise.
 * Editorial `title` alone doesn't rescue a stub (many seed-only rows
 * have only a title populated and nothing else beneath it).
 */
export function isStubCourse(d) {
  if (!d) return false;
  const hasAdmissions =
    d.admissions && typeof d.admissions === "object" && Object.keys(d.admissions).length > 0;
  const hasHesa =
    d.hesa_stats && typeof d.hesa_stats === "object" && Object.keys(d.hesa_stats).length > 0;
  const c = d.cost || {};
  const hasCost =
    c.tuition_intl_annual_usd != null ||
    c.tuition_domestic_annual_usd != null ||
    c.tuition_intl_annual_gbp != null ||
    c.tuition_domestic_annual_gbp != null;
  return !hasAdmissions && !hasHesa && !hasCost;
}

export default function LimitedDataBanner({ degree, uni }) {
  if (!isStubCourse(degree)) return null;
  const uniName = uni?.name || degree?.university_name || "the university";
  const uniHref = uni?.id ? `/uni/${uni.id}` : null;
  // UCAS is UK-only; CAO is the Irish equivalent. For every other
  // country the "Find on UCAS" chip is actively misleading, so we
  // substitute a country-appropriate directory link or hide it.
  // Country source: `uni.country` (two-letter code "UK"/"IE" or full
  // name) → `uni_id` ISO prefix as a fallback for seed rows without
  // a `uni` payload.
  const _countryRaw = (uni?.country || "").toString().toLowerCase();
  const _idPrefix = (uni?.id || degree?.university_id || "").slice(0, 3);
  const isUK = _countryRaw === "uk" || _countryRaw === "united kingdom" || _idPrefix === "uk-";
  const isIE = _countryRaw === "ie" || _countryRaw === "ireland"       || _idPrefix === "ie-";
  const directory = isUK
    ? { label: "Find on UCAS",
        href: `https://digital.ucas.com/search?query=${encodeURIComponent(
          [uniName, degree?.official_title].filter(Boolean).join(" ").trim())}`,
        name: "UCAS",
        testId: "limited-data-ucas-link" }
    : isIE
    ? { label: "Find on CAO",
        href: `https://www.cao.ie/courses.php?type=Level%208&yr=2027&search=${encodeURIComponent(
          degree?.official_title || "")}`,
        name: "CAO",
        testId: "limited-data-cao-link" }
    : null;

  return (
    <div
      data-testid="limited-data-banner"
      className="mt-6 rounded-xl border border-amber-300 bg-amber-50/70 p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-900 text-amber-50">
          <Info className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-underground-sc tracking-[0.18em] text-amber-900/70">
            Limited data for this course
          </div>
          <h3 className="mt-1 font-editorial text-xl text-stone-900 leading-tight">
            We're still gathering data for this course
          </h3>
          <p className="mt-2 text-sm text-stone-700 leading-relaxed max-w-2xl">
            Our editors haven't finished sourcing admissions, cost and outcomes
            data for <b>{degree?.official_title || "this course"}</b> yet.
            The information below is inherited from {uniName} — for the very
            latest, please check {directory ? `${directory.name} or ` : ""}
            {uniName}'s own course page.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {uniHref && (
              <Link
                to={uniHref}
                data-testid="limited-data-uni-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-white px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100"
              >
                <ExternalLink className="w-3 h-3" /> {uniName} on WUA
              </Link>
            )}
            {directory && (
              <a
                href={directory.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={directory.testId}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-white px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100"
              >
                <ExternalLink className="w-3 h-3" /> {directory.label}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
